/**
 * DynamicFields API Service
 * Handles all API calls related to dynamic field configuration and submissions
 */

import callAPI from '../ApiConfig';
import Cookies from 'universal-cookie';

const cookies = new Cookies();

/**
 * Build auth query params for Wialon validation
 */
const getAuthParams = () => {
    return {
        username: cookies.get('user_name'),
        user_id: cookies.get('user_id'),
        account_id: cookies.get('account_id'),
        account_name: cookies.get('account_name'),
        sid: cookies.get('sid'),
        baseurl: cookies.get('baseUrl'),
    };
};

/**
 * Get all field configurations for the current account (including inactive)
 */
export const getFieldConfig = async () => {
    const authParams = getAuthParams();
    return callAPI({
        route: `dynamic_fields/config`,
        method: 'GET',
        params: {
            ...authParams,
            include_inactive: true
        },
    });
};

/**
 * Create a new field configuration
 */
export const createFieldConfig = async (fieldData) => {
    const authParams = getAuthParams();
    return callAPI({
        route: `dynamic_fields/config`,
        method: 'POST',
        params: authParams,
        data: {
            ...fieldData,
            account_id: authParams.account_id,
        },
    });
};

/**
 * Update an existing field configuration
 */
export const updateFieldConfig = async (fieldData) => {
    const authParams = getAuthParams();
    return callAPI({
        route: `dynamic_fields/config`,
        method: 'POST',
        params: authParams,
        data: {
            ...fieldData,
            account_id: authParams.account_id,
        },
    });
};

/**
 * Delete a field configuration (soft delete)
 */
export const deleteFieldConfig = async (fieldId) => {
    const authParams = getAuthParams();
    return callAPI({
        route: `dynamic_fields/config/${fieldId}`,
        method: 'DELETE',
        params: authParams,
    });
};

/**
 * Restore a soft-deleted field configuration
 */
export const restoreFieldConfig = async (fieldId) => {
    const authParams = getAuthParams();
    return callAPI({
        route: `dynamic_fields/config/${fieldId}/restore`,
        method: 'PUT',
        params: authParams,
    });
};

/**
 * Get submissions with filters
 */
export const getSubmissions = async (filters = {}) => {
    const authParams = getAuthParams();
    return callAPI({
        route: `dynamic_fields/submissions`,
        method: 'GET',
        params: {
            ...authParams,
            start_date: filters.startDate,
            end_date: filters.endDate,
            search: filters.search,
            offset: filters.offset || 0,
            limit: filters.limit || 50,
        },
    });
};

/**
 * Save a submission (typically called from mobile app, but available here for testing)
 */
export const saveSubmission = async (submissionData) => {
    const authParams = getAuthParams();
    return callAPI({
        route: `dynamic_fields/submissions`,
        method: 'POST',
        params: authParams,
        data: {
            ...submissionData,
            account_id: authParams.account_id,
            wialon_user_id: authParams.user_id,
        },
    });
};

/**
 * Reorder field configurations (bulk update display_order)
 */
export const reorderFields = async (fields) => {
    const authParams = getAuthParams();
    return callAPI({
        route: `dynamic_fields/config/reorder`,
        method: 'PUT',
        params: authParams,
        data: { fields },
    });
};

export default {
    getFieldConfig,
    createFieldConfig,
    updateFieldConfig,
    deleteFieldConfig,
    restoreFieldConfig,
    getSubmissions,
    saveSubmission,
    reorderFields,
};
